'use client';

import * as React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * ErrorState — canonical fetch/system-error rendering for panels and pages.
 *
 * Mirrors EmptyState's shape (icon chip → title → description → action) so
 * error and empty states read as one family. Always offers a recovery action
 * when the caller can retry — a system error without a next step is a dead end.
 *
 *   {error ? (
 *     <ErrorState description={error} onRetry={load} />
 *   ) : …}
 */

export interface ErrorStateProps {
  title?: string;
  /** What went wrong, in user terms. Falls back to a generic message. */
  description?: string;
  /** Renders a retry button wired to this handler. */
  onRetry?: () => void;
  retryLabel?: string;
  /** Extra action rendered next to (or instead of) retry. */
  action?: React.ReactNode;
  /** `sm` for inline panel errors, `md` (default) for full-page/section. */
  size?: 'sm' | 'md';
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  description = 'We could not load this data. Please try again.',
  onRetry,
  retryLabel = 'Try again',
  action,
  size = 'md',
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'py-8 px-4 gap-2' : 'py-16 px-6 gap-3',
        className,
      )}
    >
      <div
        className={cn(
          'rounded-full bg-destructive/10 flex items-center justify-center',
          size === 'sm' ? 'w-10 h-10' : 'w-14 h-14',
        )}
      >
        <AlertCircle
          aria-hidden="true"
          className={cn('text-destructive', size === 'sm' ? 'w-5 h-5' : 'w-7 h-7')}
        />
      </div>
      <h3 className={cn('font-semibold text-foreground', size === 'sm' ? 'text-sm' : 'text-base')}>
        {title}
      </h3>
      <p className={cn('text-muted-foreground max-w-md', size === 'sm' ? 'text-xs' : 'text-sm')}>
        {description}
      </p>
      {(onRetry || action) && (
        <div className={cn('flex items-center gap-2', size === 'sm' ? 'mt-1' : 'mt-2')}>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw aria-hidden="true" />
              {retryLabel}
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}
